/**
 * Shared navigation configuration for Admin + Employee panels.
 *
 * Each entry declares:
 *   - label:       visible menu label
 *   - path:        relative path (used under /dashboard/* for admin or /employee/* for employee)
 *   - permission:  permission key from the `employees.permissions` JSONB map
 *                  (null = always visible, admin always passes regardless of key)
 *   - icon:        lucide-react icon name (string; caller imports from lucide)
 *
 * The `EmployeeDashboard` and `AdminLayout` sidebars consume this so they never
 * drift out of sync. When you add a new section to Admin, add it here once and
 * it is automatically visible to any employee whose permission flag is true.
 */

export const NAV_ITEMS = [
  { label: "Overview",       path: "overview",       permission: null,                   icon: "LayoutDashboard", employeeOnly: false },
  { label: "My Referrals",   path: "my-referrals",   permission: null,                   icon: "Ticket",          employeeOnly: true  },
  { label: "Customers",      path: "users",          permission: "view_customers",       icon: "Users" },
  { label: "KYC / Aadhaar",  path: "aadhaar-queue",  permission: "verify_kyc",           icon: "ShieldCheck" },
  { label: "Applications",   path: "applications",   permission: "view_applications",    icon: "FileText" },
  { label: "Disbursements",  path: "disbursements",  permission: "view_disbursements",   icon: "Banknote" },
  { label: "Agents",         path: "agents",         permission: "view_agents",          icon: "UserCog" },
  { label: "Commissions",    path: "commissions",    permission: "view_commissions",     icon: "DollarSign" },
  { label: "Reports",        path: "reports",        permission: "view_reports",         icon: "BarChart3" },
];

/**
 * Permission keys used for *write* actions. These gate action buttons (Approve,
 * Reject, Transfer, etc.) inside admin pages that are shared with employees.
 *
 * Admin role always passes — employees need the specific key = true.
 */
export const WRITE_PERMISSIONS = {
  approveApplications: "approve_applications",
  verifyKyc:           "verify_kyc",
  approveDisbursements:"approve_disbursements",
};

/**
 * Human-readable label for each permission key — used on the Admin → Employees
 * Create/Edit form to describe what each toggle grants.
 */
export const PERMISSION_LABELS = {
  view_customers:       { label: "View Customers",       description: "Can see customer list and profiles" },
  view_applications:    { label: "View Applications",    description: "Can see all loan applications" },
  approve_applications: { label: "Approve Applications", description: "Can approve or reject applications (requires View Applications)" },
  verify_kyc:           { label: "Verify KYC",           description: "Can approve or reject Aadhaar documents" },
  view_disbursements:   { label: "View Disbursements",   description: "Can see disbursement list" },
  approve_disbursements:{ label: "Approve Disbursements",description: "Can approve and mark disbursements transferred" },
  view_agents:          { label: "View Agents",          description: "Can see agent list and referral stats" },
  view_commissions:     { label: "View Commissions",     description: "Can see commission records" },
  view_reports:         { label: "View Reports",         description: "Can access reports section" },
};

/**
 * Returns true if the given user (admin or employee) can perform the write
 * action guarded by the given permission key. Admin roles always pass.
 *
 * Usage in an admin page:
 *    const { user } = useAuth();
 *    const canApprove = canWrite(user, 'approve_applications');
 *    {canApprove && <ApproveButton/>}
 */
export function canWrite(user, permissionKey) {
  if (!user) return false;
  const role = user.role;
  if (role === "super_admin" || role === "ops_staff" || role === "admin") return true;
  if (role === "employee") {
    const perms = user.permissions || {};
    return perms[permissionKey] === true;
  }
  return false;
}

/**
 * Returns true if the user is admin (super_admin or ops_staff). Use this to
 * guard truly admin-only buttons (block-user, delete-employee, etc.) that are
 * not controlled by any employee permission flag.
 */
export function isAdminRole(user) {
  if (!user) return false;
  return user.role === "super_admin" || user.role === "ops_staff" || user.role === "admin";
}
